import { CluFan } from './candy-tile.component';
import { candyTileProps } from './candy-tile.util';
import { AppIcon } from '@/components/ui/app-icon';
import { iconMagnifyingGlass, iconSpinnerThird } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import {
  rectSortingStrategy,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  forwardRef,
  useMemo,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { LockMark } from '../lock-mark.component';
import type { LockedUpgradeClick } from '../use-locked-upgrade-click.hook';
import { ChatTitleField } from './chat-title-field.component';
import { PinIcon } from './pin-icon.component';
import { DropLine } from './sidebar-dnd.component';
import { dragId, dropLineFor, useSidebarDrag } from './sidebar-dnd.util';
import { focusFirst } from './sidebar-focus.util';
import { AddPageButton, SidebarItemMenu } from './sidebar-item-menu.component';
import type { SidebarMenuPlace } from './sidebar-menu.util';
import { SidebarTooltip } from './sidebar-tooltip.component';
import {
  itemLabel,
  RAIL_CELL,
  RAISED_CARD,
  SIDEBAR_FILL,
  SIDEBAR_HOVER,
  SIDEBAR_ICON,
  TILE_FILL,
  TILE_TRANSITION,
} from './tabs-sidebar.styles';
import { useRenameInPlace } from './use-rename-in-place.hook';
import { useDropZone, useSortablePin } from './use-sortable-pin.hook';
import type { TabsNavItem } from './use-tabs-nav.hook';

const FOCUS_RING =
  'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50';

const FAVORITE: SidebarMenuPlace = { kind: 'pin', section: 'favorites' };
const PINNED: SidebarMenuPlace = { kind: 'pin', section: 'pinned' };

/**
 * A tile's fill: raised when selected, else translucent white that lights up
 * on hover or keyboard focus.
 */
const tileFill = (selected: boolean) => (selected ? RAISED_CARD : TILE_FILL);

/** Opens the ⌘K command palette mounted at the router root, as the top bar did. */
function openCommandPalette() {
  window.dispatchEvent(new Event('cleanlist:open-command-palette'));
}

/**
 * "Search or ask Clu": the ⌘K palette, moved out of the top bar. Folded to
 * the page rail, it is one of the rail's cells, keeping the bar's fill.
 */
export function CommandBar({ collapsed = false }: { collapsed?: boolean }) {
  if (collapsed) {
    return (
      <SidebarTooltip label='Search or ask Clu'>
        <button
          type='button'
          aria-label='Search or ask Clu'
          onClick={openCommandPalette}
          className={cn(
            'grid shrink-0 place-items-center transition-colors',
            'mt-2',
            RAIL_CELL,
            'hover:bg-white/80 dark:hover:bg-white/[0.09]',
            FOCUS_RING,
            SIDEBAR_FILL,
            SIDEBAR_ICON
          )}
        >
          <AppIcon icon={iconMagnifyingGlass} style={{ fontSize: 16 }} />
        </button>
      </SidebarTooltip>
    );
  }
  return (
    <button
      type='button'
      onClick={openCommandPalette}
      className={cn(
        'mt-2 flex h-9 w-full shrink-0 cursor-text items-center gap-2 rounded-[10px] pr-2 pl-2.5 text-left transition-colors',
        'text-gray-600 hover:bg-white/80 dark:text-gray-400 dark:hover:bg-white/[0.09]',
        FOCUS_RING,
        SIDEBAR_FILL
      )}
    >
      <AppIcon icon={iconMagnifyingGlass} style={{ fontSize: 15 }} />
      <span className='flex-1 truncate'>Search or ask Clu</span>
      <kbd className='rounded-[5px] border border-black/10 bg-white px-[5px] py-px font-sans text-xs text-foreground dark:border-white/10 dark:bg-white/[0.08]'>
        ⌘K
      </kbd>
    </button>
  );
}

const lockedNote = (item: TabsNavItem) =>
  item.lockedTooltip ?? 'Upgrade to unlock';

/**
 * A locked entry's name and its upgrade note, as in "Integrations · Available
 * on Pro+".
 */
const lockedName = (item: TabsNavItem) => `${item.label} · ${lockedNote(item)}`;

/**
 * A pin's click: for a locked page, the sidebar's locked click with the
 * page's id; undefined for anything else, which follows its link.
 */
function lockedPinClick(
  item: TabsNavItem,
  onLockedClick: LockedUpgradeClick | undefined
) {
  const { ref } = item;
  if (!item.locked || ref.kind !== 'page' || !onLockedClick) return undefined;
  return (event: MouseEvent<HTMLElement>) => onLockedClick(ref.id, event);
}

/**
 * A locked item's name for screen readers: its label and its upgrade note,
 * as "Integrations · Available on Pro+". The note is in the name, not the
 * tooltip's description, since dragging takes that description. Undefined
 * when the text on screen says it all.
 */
function itemName(item: TabsNavItem): string | undefined {
  return item.locked ? lockedName(item) : undefined;
}

type PinTargetProps = {
  item: TabsNavItem;
  /** Where the pin sits, for the click event: a tile, a row or a chat tab. */
  placement: 'tile' | 'row' | 'tab';
  onOpenClu: (id: string) => void;
  className?: string;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLElement>, 'children' | 'className'>;

/**
 * What a pin opens: its page or agent, or its chat (an agent's chat in the
 * agent's workspace, a Clu chat on /clu).
 */
export const PinTarget = forwardRef<HTMLElement, PinTargetProps>(
  function PinTarget(
    {
      item,
      placement,
      onOpenClu,
      className,
      children,
      onClick: onPinClick,
      ...rest
    },
    ref
  ) {
    const posthog = usePostHog();
    // Every pin click, wherever it sits. Ids and names stay out: a chat's
    // title or an agent's name is the user's own text.
    const onClick = (event: MouseEvent<HTMLElement>) => {
      posthog?.capture('sidebar_nav_clicked', {
        placement,
        kind: item.ref.kind,
        page: item.ref.kind === 'page' ? item.ref.id : undefined,
        locked: Boolean(item.locked),
      });
      onPinClick?.(event);
    };
    const chat = item.chat;
    if (chat && !chat.agentId)
      return (
        <button
          ref={ref as Ref<HTMLButtonElement>}
          type='button'
          {...rest}
          onClick={(event) => {
            onClick?.(event);
            if (!event.defaultPrevented) onOpenClu(chat.id);
          }}
          className={className}
        >
          {children}
        </button>
      );
    if (chat?.agentId)
      return (
        <Link
          ref={ref as Ref<HTMLAnchorElement>}
          to='/agents/$agentId'
          params={{ agentId: chat.agentId }}
          search={{ chat: chat.id }}
          {...rest}
          onClick={onClick}
          className={className}
        >
          {children}
        </Link>
      );
    return (
      <Link
        ref={ref as Ref<HTMLAnchorElement>}
        to={item.href}
        {...rest}
        onClick={onClick}
        className={className}
      >
        {children}
      </Link>
    );
  }
);

/** A pinned chat's spinner while its reply streams, on an open row. */
export function RunningMark() {
  return (
    <span role='img' aria-label='Running' className='shrink-0'>
      <AppIcon
        icon={iconSpinnerThird}
        className='animate-spin text-primary'
        style={{ fontSize: 13 }}
      />
    </span>
  );
}

const NO_RUNNING_CHATS: ReadonlySet<string> = new Set();

export interface PinListProps {
  items: readonly TabsNavItem[];
  selectedKey: string | null;
  /** The pinned chats whose reply is streaming (CLE-567: several can). */
  runningChatIds?: ReadonlySet<string>;
  onOpenClu: (id: string) => void;
  /** Renames a pinned chat, from its row. */
  onRenameChat?: (id: string, title: string) => void;
  /** What a click on a locked page does; see `useLockedUpgradeClick`. */
  onLockedClick?: LockedUpgradeClick;
}

/**
 * The tile grid: three 76 by 52 tiles to a row, 6px apart, radius 10, a 20px
 * icon 3px above a 12px label (Lev, 2026-10-30). New chat is the first row
 * of the tabs.
 */
export function FavoriteTiles({
  items,
  selectedKey,
  runningChatIds = NO_RUNNING_CHATS,
  onOpenClu,
  onLockedClick,
}: PinListProps) {
  const { zoneRef, dragging, emptyLine } = useDropZone(
    'favorites',
    items.length === 0
  );
  const { target } = useSidebarDrag();
  const ids = useMemo(
    () => items.map((item) => dragId('favorites', item.key)),
    [items]
  );
  if (items.length === 0 && !dragging) return null;
  const lastKey = items.at(-1)?.key ?? null;
  return (
    <SortableContext id='favorites' items={ids} strategy={rectSortingStrategy}>
      <div
        ref={zoneRef}
        role='list'
        aria-label='Favorites'
        className={cn(
          'relative grid grid-cols-3 gap-1.5 text-center text-[12px] leading-[15px]',
          dragging && items.length === 0 && 'min-h-8'
        )}
      >
        {items.map((item) => (
          <PinTile
            key={item.key}
            item={item}
            selected={item.key === selectedKey}
            running={Boolean(item.chat && runningChatIds.has(item.chat.id))}
            onOpenClu={onOpenClu}
            onLockedClick={onLockedClick}
            line={dropLineFor(target, 'favorites', item.key, lastKey)}
          />
        ))}
        {emptyLine && <DropLine position='before' across={false} />}
      </div>
    </SortableContext>
  );
}

function PinTile({
  item,
  selected,
  running,
  onOpenClu,
  onLockedClick,
  line,
}: {
  item: TabsNavItem;
  selected: boolean;
  running: boolean;
  onOpenClu: (id: string) => void;
  onLockedClick?: LockedUpgradeClick;
  line: 'before' | 'after' | null;
}) {
  const { nodeRef, handleProps, isDragging } = useSortablePin(
    'favorites',
    item,
    'tile'
  );
  const lockedClick = lockedPinClick(item, onLockedClick);
  const tile = (
    <PinTarget
      item={item}
      placement='tile'
      onOpenClu={onOpenClu}
      {...handleProps}
      onClick={lockedClick}
      data-pin-key={item.key}
      aria-label={itemName(item)}
      aria-current={selected ? 'page' : undefined}
      {...candyTileProps(item.iconName, 10)}
      className={cn(
        'relative flex h-[52px] w-full min-w-0 flex-col items-center justify-center gap-[3px] rounded-[10px] px-0.5 text-gray-700 dark:text-gray-300',
        TILE_TRANSITION,
        FOCUS_RING,
        selected && 'text-foreground dark:text-foreground',
        tileFill(selected)
      )}
    >
      {item.locked && <LockMark className='absolute top-[7px] right-[7px]' />}
      <span className='tile-icon grid h-[20px] place-items-center text-foreground'>
        {running ? (
          <RunningMark />
        ) : (
          <CluFan iconName={item.iconName} size={20}>
            <PinIcon
              item={item}
              size={item.agentId || item.chat ? 21 : 20}
              filled={selected}
            />
          </CluFan>
        )}
      </span>
      <span className='flex w-full items-center justify-center'>
        <span className={cn('truncate', itemLabel(selected))}>
          {item.label}
        </span>
      </span>
    </PinTarget>
  );
  return (
    <SidebarItemMenu
      ref={nodeRef}
      sidebarRef={item.ref}
      label={item.label}
      place={FAVORITE}
      role='listitem'
      className={cn('grid min-w-0', isDragging && 'opacity-40')}
    >
      {item.locked ? (
        <SidebarTooltip label={lockedNote(item)}>{tile}</SidebarTooltip>
      ) : (
        tile
      )}
      {line && <DropLine position={line} across />}
    </SidebarItemMenu>
  );
}

/**
 * The pinned rows: 32px rows. A "+" after them adds back a page missing from
 * the sidebar, and a chat's row is renamed in place.
 */
export function PinnedRows({
  items,
  selectedKey,
  runningChatIds = NO_RUNNING_CHATS,
  onOpenClu,
  onRenameChat,
  onLockedClick,
}: PinListProps) {
  const { zoneRef, dragging, emptyLine } = useDropZone(
    'pinned',
    items.length === 0
  );
  const { target } = useSidebarDrag();
  const ids = useMemo(
    () => items.map((item) => dragId('pinned', item.key)),
    [items]
  );
  const lastKey = items.at(-1)?.key ?? null;
  return (
    <SortableContext
      id='pinned'
      items={ids}
      strategy={verticalListSortingStrategy}
    >
      <nav
        ref={zoneRef}
        aria-label='Pinned'
        className={cn(
          'group/pinned relative flex flex-col',
          dragging && items.length === 0 && 'min-h-8'
        )}
      >
        {items.map((item) => (
          <PinRow
            key={item.key}
            item={item}
            selected={item.key === selectedKey}
            running={Boolean(item.chat && runningChatIds.has(item.chat.id))}
            onOpenClu={onOpenClu}
            onRenameChat={onRenameChat}
            onLockedClick={onLockedClick}
            line={dropLineFor(target, 'pinned', item.key, lastKey)}
          />
        ))}
        {emptyLine && <DropLine position='before' across={false} />}
        <AddPageButton />
      </nav>
    </SortableContext>
  );
}

function PinRow({
  item,
  selected,
  running,
  onOpenClu,
  onRenameChat,
  onLockedClick,
  line,
}: {
  item: TabsNavItem;
  selected: boolean;
  running: boolean;
  onOpenClu: (id: string) => void;
  onRenameChat?: (id: string, title: string) => void;
  onLockedClick?: LockedUpgradeClick;
  line: 'before' | 'after' | null;
}) {
  const chat = item.chat;
  const { editing, start, done } = useRenameInPlace({
    id: chat && !chat.pending ? chat.id : null,
    rename: onRenameChat,
    focusRow: () => focusFirst([item.key], document),
  });
  const { nodeRef, handleProps, isDragging } = useSortablePin(
    'pinned',
    item,
    'row',
    editing
  );
  const lockedClick = lockedPinClick(item, onLockedClick);
  const row = editing ? (
    <div
      className={cn(
        'flex h-8 w-full shrink-0 items-center gap-2 rounded-lg pr-1 pl-2 text-foreground',
        selected && cn(RAISED_CARD, 'font-semibold')
      )}
    >
      <span
        className={cn('grid w-4 shrink-0 place-items-center', SIDEBAR_ICON)}
      >
        <PinIcon item={item} size={16} filled={selected} />
      </span>
      <ChatTitleField title={item.label} onDone={done} className='h-6' />
    </div>
  ) : (
    <PinTarget
      item={item}
      placement='row'
      onOpenClu={onOpenClu}
      {...handleProps}
      onClick={lockedClick}
      data-pin-key={item.key}
      aria-label={itemName(item)}
      aria-current={selected ? 'page' : undefined}
      className={cn(
        'flex h-8 w-full shrink-0 items-center gap-2 rounded-lg px-2 text-left text-foreground transition-colors group-focus-within/pin:pr-8 group-hover/pin:pr-8',
        FOCUS_RING,
        selected ? RAISED_CARD : SIDEBAR_HOVER
      )}
    >
      <span
        className={cn('grid w-4 shrink-0 place-items-center', SIDEBAR_ICON)}
      >
        <PinIcon item={item} size={16} filled={selected} />
      </span>
      <span className={cn('min-w-0 flex-1 truncate', itemLabel(selected))}>
        {item.label}
      </span>
      {running && <RunningMark />}
      {item.locked && <LockMark className='mr-0.5' />}
    </PinTarget>
  );
  return (
    <SidebarItemMenu
      ref={nodeRef}
      sidebarRef={item.ref}
      label={item.label}
      place={PINNED}
      button
      onRename={start}
      editing={editing}
      className={cn('flex', isDragging && 'opacity-40')}
    >
      {!editing && item.locked ? (
        <SidebarTooltip label={lockedNote(item)}>{row}</SidebarTooltip>
      ) : (
        row
      )}
      {line && <DropLine position={line} across={false} />}
    </SidebarItemMenu>
  );
}
